import { Button } from "@/components/ui/button";
import { useAnalyticsConsent } from "@/hooks/useAnalyticsConsent";
import { COOKIE_POLICY_URL, PRIVACY_POLICY_URL } from "@/lib/consent";
import { cn } from "@/lib/utils";

const LINK =
  "font-medium text-k-accent-ink underline underline-offset-2 hover:text-k-ink-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-k-accent rounded-sm";

const CHOICE =
  "h-8 min-w-[88px] flex-1 rounded-lg border-k-line-2 bg-k-card px-3 font-k-display text-[13px] font-semibold text-k-ink-hi hover:border-k-ink-3 hover:bg-k-surface sm:flex-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-k-accent focus-visible:ring-0";

/**
 * Analytics opt-in (ADR-051 §5). Shown on every route — public pages
 * included — until the user picks Accept or Decline; the two buttons are
 * styled identically so declining is exactly as easy as accepting.
 *
 * Non-blocking: no overlay, no focus trap, no autofocus. It is `sticky`
 * at the end of the document rather than `fixed`, so on small screens
 * the page can always be scrolled far enough that it covers nothing.
 */
export function ConsentBanner() {
  const { consent, setConsent } = useAnalyticsConsent();

  if (consent !== "unset") return null;

  return (
    <div className="pointer-events-none sticky bottom-0 z-50 flex justify-end p-3 sm:p-4">
      <section
        aria-label="Analytics consent"
        className={cn(
          "pointer-events-auto w-full max-w-md rounded-[10px] border border-k-line bg-k-card p-4 shadow-k-md",
          "font-k-sans text-[13px] leading-relaxed text-k-ink-2",
        )}
      >
        <p>
          We use optional analytics to measure dashboard performance and help
          with support. Nothing is collected unless you accept. See our{" "}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK}
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href={COOKIE_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK}
          >
            Cookie Policy
          </a>
          .
        </p>
        <div className="mt-3 flex gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className={CHOICE}
            onClick={() => setConsent(false)}
          >
            Decline
          </Button>
          <Button
            type="button"
            variant="outline"
            className={CHOICE}
            onClick={() => setConsent(true)}
          >
            Accept
          </Button>
        </div>
      </section>
    </div>
  );
}
